import {
  Directive,
  ElementRef,
  HostListener,
  inject,
  input,
  OnDestroy,
  effect,
  ComponentRef,
} from '@angular/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { TooltipComponent } from './tooltip.component';

@Directive({
  selector: '[app-tool-tip]',
  standalone: true,
})
export class TooltipDirective implements OnDestroy {
  private overlay = inject(Overlay);
  private elementRef = inject(ElementRef);

  text = input.required<string | null>({ alias: 'tooltip-text' });
  size = input<'sm' | 'md' | 'lg'>('md', { alias: 'tooltip-size' });
  color = input<'primary' | 'info' | 'success' | 'warning' | 'danger'>(
    'primary',
    { alias: 'tooltip-color' }
  );

  private overlayRef: OverlayRef | null = null;
  private componentRef: ComponentRef<TooltipComponent> | null = null;

  constructor() {
    effect(() => {
      const currentText = this.text();
      const currentSize = this.size();
      const currentColor = this.color();

      if (this.componentRef) {
        this.componentRef.setInput('text', currentText ?? '');
        this.componentRef.setInput('size', currentSize);
        this.componentRef.setInput('color', currentColor);
      }
    });
  }

  @HostListener('mouseenter')
  show() {
    if (!this.text()) return;

    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(this.elementRef)
      .withPositions([
        {
          originX: 'center',
          originY: 'top',
          overlayX: 'center',
          overlayY: 'bottom',
          offsetY: -8,
        },
        {
          originX: 'center',
          originY: 'bottom',
          overlayX: 'center',
          overlayY: 'top',
          offsetY: 8,
        },
        {
          originX: 'start',
          originY: 'top',
          overlayX: 'start',
          overlayY: 'bottom',
          offsetY: -8,
        },
        {
          originX: 'end',
          originY: 'top',
          overlayX: 'end',
          overlayY: 'bottom',
          offsetY: -8,
        }
      ]);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
    });

    const tooltipPortal = new ComponentPortal(TooltipComponent);
    this.componentRef = this.overlayRef.attach(tooltipPortal);

    this.componentRef.setInput('text', this.text() ?? '');
    this.componentRef.setInput('size', this.size());
    this.componentRef.setInput('color', this.color());
  }

  @HostListener('mouseleave')
  hide() {
    this.destroyOverlay();
  }

  ngOnDestroy() {
    this.destroyOverlay();
  }

  private destroyOverlay() {
    if (this.overlayRef) {
      this.overlayRef.detach();
      this.overlayRef.dispose();
      this.overlayRef = null;
      this.componentRef = null;
    }
  }
}
